import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { Container } from 'reactstrap';
import { UploadSection } from './UploadSection';
import { StyleGuideChecker } from './StyleGuideChecker';
import { SessionSidebar } from './SessionSidebar';
import PortalPageHeader from '../../common/PortalPageHeader';
import { PageDetails } from '../config';
import { getData, postData } from '../../../DataAccessLayer';
import { pollingStatus } from '../../common/utils';
import { useNavigate, useParams } from 'react-router-dom';
import { esga } from '../../../DataAccessLayer/services';
import {
  unzipDocx,
  readFileAsArrayBuffer,
  getTextFromZip,
  parseDocXmlToParagraphs,
} from './utils/docx';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { WithSmallScreen } from '../../common/WithSmallScreen';

export const ESGA = WithSmallScreen((props) => {
  const id = 'esga';
  const [title, setTitle] = useState('');
  const { sessionId } = useParams();

  const isUploadAllowed = true;
  const checkerRef = useRef(null);
  const [isUploaded, setIsUploaded] = useState(false);
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [docBuffer, setDocBuffer] = useState(null);

  /* ── Sidebar state ── */
  const [sessions, setSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const isMobile = props.isSmall;
  const navigate = useNavigate();

  /* ── Auto-collapse sidebar when entering mobile breakpoint ── */
  useEffect(() => {
    if (isMobile) setIsSidebarOpen(false);
  }, [isMobile]);

  /* ── Page title ── */
  useEffect(() => {
    const page = Object.values(PageDetails).find((p) => p.id === id);
    if (page) setTitle(page.title);
  }, []);

  /* ══════════════════════════════════════════════════════════════
   *  Fetch session history for the sidebar
   * ══════════════════════════════════════════════════════════════ */

  const fetchSessionHistory = useCallback(async () => {
    setIsLoadingSessions(true);
    try {
      const data = await getData(esga.getSessions, true);
      if (Array.isArray(data)) {
        const sorted = [...data].sort((a, b) => new Date(b.UPDATED_AT) - new Date(a.UPDATED_AT));
        setSessions(sorted);
      }
    } catch (error) {
      toast.error('Failed to fetch session history.');
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  useEffect(() => {
    fetchSessionHistory();
  }, [fetchSessionHistory]);

  /* ══════════════════════════════════════════════════════════════
   *  When sessionId is in the URL: fetch doc + poll for analysis
   * ══════════════════════════════════════════════════════════════ */

  useEffect(() => {
    if (!sessionId) {
      setIsUploaded(false);
      setSessionData(null);
      setDocBuffer(null);
      return;
    }

    setIsUploaded(true);
    setIsLoadingSession(true);

    // Fetch the docx binary from S3 for preview
    fetchDoc();

    // Poll session until Gemini response is ready
    const stop = pollingStatus({
      fetchFn: fetchSession,
      validate: (data) => data?.STATUS === 'COMPLETED' || data?.STATUS === 'FAILED' || !data,
    });

    return () => stop();
  }, [sessionId]);

  // ── Load doc preview, then feed issues when both are ready ──
  const docLoadedRef = useRef(false);
  const issuesLoadedRef = useRef(false);

  useEffect(() => {
    if (!docBuffer || !checkerRef.current?.loadFile) return;

    docLoadedRef.current = false;
    issuesLoadedRef.current = false;

    checkerRef.current
      .loadFile(docBuffer)
      .then(() => {
        docLoadedRef.current = true;

        // If Gemini response is already available, load issues now
        if (
          !issuesLoadedRef.current &&
          sessionData?.STATUS === 'COMPLETED' &&
          sessionData?.RESPONSE &&
          checkerRef.current?.loadIssues
        ) {
          issuesLoadedRef.current = true;
          setIsLoadingSession(false);
          checkerRef.current.loadIssues(sessionData);
        }
      })
      .catch((error) => {
        console.error(error);
        toast.error('Unable to open the document. Please try again with a valid .docx file.');
        setIsUploaded(false);
      });
  }, [docBuffer]);

  // ── If Gemini response arrives after doc is already loaded ──
  useEffect(() => {
    if (
      docLoadedRef.current &&
      !issuesLoadedRef.current &&
      sessionData?.STATUS === 'COMPLETED' &&
      sessionData?.RESPONSE &&
      checkerRef.current?.loadIssues
    ) {
      issuesLoadedRef.current = true;
      setIsLoadingSession(false);
      checkerRef.current.loadIssues(sessionData);
    }

    if (
      sessionData?.STATUS === 'FAILED' ||
      (sessionData?.STATUS === 'COMPLETED' && !sessionData?.RESPONSE)
    ) {
      toast.error('Error getting response. Please upload document again.');
      setIsLoadingSession(false);
      setIsUploaded(false);
      // navigate('/monarchMind/esga');
      navigate('/esga');
    }
  }, [sessionData]);

  // ── Fetch session data (called by polling) ──
  const fetchSession = async () => {
    try {
      const data = await getData(esga.getSession(sessionId), true);
      if (!data) {
        setIsUploaded(false);
        toast.error('No session data found');
        return null;
      }
      setSessionData(data);
      return data;
    } catch (err) {
      toast.error('Error fetching session.');
      return null;
    }
  };

  // ── Fetch doc binary from S3 ──
  const fetchDoc = async () => {
    try {
      const data = await getData(esga.getDoc(sessionId), true, true);
      setDocBuffer(data);
    } catch (error) {
      toast.error('Failed to load document.');
      setIsLoadingSession(false);
      setIsUploaded(false);
      // navigate('/monarchMind/esga');
      navigate('/esga');
    }
  };

  // ── Upload handler ──
  const handleUpload = async (file) => {
    if (!file.name.toLowerCase().endsWith('.docx')) {
      toast.error('Please upload a .docx file for preview and suggestions.');
      return;
    }

    try {
      setIsLoadingSession(true);

      const buffer = await readFileAsArrayBuffer(file);
      const zip = await unzipDocx(buffer);
      const xml = await getTextFromZip(zip, 'word/document.xml');
      const { paragraphs: parsedParagraphs } = parseDocXmlToParagraphs(xml);

      const content = {
        paragraphs: parsedParagraphs.map((paragraph) => ({
          paragraphId: paragraph.paragraphId,
          text: paragraph.text,
        })),
      };

      const form = new FormData();
      form.append('file', file);
      form.append('content', JSON.stringify(content));

      const data = await postData(esga.upload, form, true);

      if (data?.SESSION_ID) {
        fetchSessionHistory();
        // navigate(`/monarchMind/esga/${data.SESSION_ID}`);
        navigate(`/esga/${data.SESSION_ID}`);
        return;
      }

      toast.error('Upload succeeded but no session ID returned.');
    } catch (error) {
      console.error(error);
      toast.error('Upload failed. Please try again.');
    } finally {
      setIsLoadingSession(false);
    }
  };

  /* ══════════════════════════════════════════════════════════════
   *  Sidebar handlers
   * ══════════════════════════════════════════════════════════════ */

  const handleToggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  const handleNewSession = useCallback(() => {
    // navigate('/monarchMind/esga');
    navigate('/esga');
  }, [navigate]);

  const handleSelectSession = useCallback(
    (selectedSessionId) => {
      // navigate(`/monarchMind/esga/${selectedSessionId}`);
      navigate(`/esga/${selectedSessionId}`);
    },
    [navigate],
  );

  /* ══════════════════════════════════════════════════════════════
   *  Render
   * ══════════════════════════════════════════════════════════════ */

  return (
    <div id={`${id}__div_pageWrapper`} className={`myOdu__${id}`}>
      <Container id={`${id}__container_pageWrapper`} className="myOdu__container secondaryPage">
        <PortalPageHeader
          title={title}
          id={id}
          breadcrumbs={PageDetails[id]?.breadcrumbs}
          tooltip={PageDetails[id]?.tooltip}
        />
        <Box
          id={`${id}__box_wrapper`}
          className="wrapper"
          sx={{
            display: 'flex',
            height: 'calc(100vh - 200px)',
            minHeight: 500,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            gap: 2,
          }}
        >
          {/* ── Session sidebar (permanent mini-variant, in-layout) ── */}
          <SessionSidebar
            sessions={sessions}
            activeSessionId={sessionId}
            isLoadingSessions={isLoadingSessions}
            isOpen={isSidebarOpen}
            onToggle={handleToggleSidebar}
            onNewSession={handleNewSession}
            onSelectSession={handleSelectSession}
          />

          <Box
            component="main"
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minWidth: 0,
              minHeight: 0,
            }}
          >
            {isMobile && !isSidebarOpen && (
              <Box sx={{ p: 0.5, flexShrink: 0 }}>
                <IconButton
                  onClick={handleToggleSidebar}
                  size="small"
                  aria-label="Open session sidebar"
                >
                  <FontAwesomeIcon icon={faBars} size="sm" />
                </IconButton>
              </Box>
            )}

            {!isUploaded ? (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  p: 0,
                  overflow: 'auto',
                }}
              >
                <UploadSection isUploadAllowed={isUploadAllowed} onUpload={handleUpload} />
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  p: 0,
                }}
              >
                <StyleGuideChecker
                  ref={checkerRef}
                  isLoading={isLoadingSession}
                  sessionData={sessionData}
                  fetchSessionHistory={fetchSessionHistory}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Container>
    </div>
  );
});
